import { FC } from 'react'
import { useDroppable } from '@dnd-kit/core'
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { FormElement } from '@mobileTypes'
import { getFormElementIcon, getFormElementSummary } from '@mobileConstants'
import { Delete, DragIndicator, Edit } from '@mui/icons-material'
import { Box, Chip, IconButton, Paper, Typography } from '@mui/material'

type AccordionParent = { index: number; isBottom: boolean }

interface ConfiguredFormPanelProps {
  elements: FormElement[]
  bottomElements: FormElement[]
  onEditElement: (index: number, isBottom: boolean, accordionParent?: AccordionParent) => void
  onDeleteElement: (index: number, isBottom: boolean, accordionParent?: AccordionParent) => void
  activeId: string | null
}

const buildItemId = (containerId: string, index: number) => `${containerId}:${index}`

const buildAccordionContainerId = (isBottom: boolean, accordionIndex: number) =>
  `ACCORDION:${isBottom ? 'BOTTOM' : 'MAIN'}:${accordionIndex}`

const ElementSortableItem: FC<{
  element: FormElement
  containerId: string
  index: number
  isBottom: boolean
  accordionParent?: AccordionParent
  onEdit: () => void
  onDelete: () => void
  onEditElement: ConfiguredFormPanelProps['onEditElement']
  onDeleteElement: ConfiguredFormPanelProps['onDeleteElement']
  activeId: string | null
}> = ({
  element,
  containerId,
  index,
  isBottom,
  accordionParent,
  onEdit,
  onDelete,
  onEditElement,
  onDeleteElement,
  activeId,
}) => {
  const itemId = buildItemId(containerId, index)
  const isAccordion = element.type === 'ACCORDION'

  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    isDragging,
  } = useSortable({
    id: itemId,
    data: {
      type: 'item',
      containerId,
      index,
      isAccordion,
      isBottom,
      accordionIndex: isAccordion && !accordionParent ? index : undefined,
    },
    transition: null,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: 'none',
    opacity: activeId === itemId || isDragging ? 0.3 : 1,
  }

  return (
    <Box ref={setNodeRef} sx={{ ...style, mb: 1 }}>
      <Paper
        variant='outlined'
        sx={{
          p: 1.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          backgroundColor: 'background.paper',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
          <Box
            ref={setActivatorNodeRef}
            {...listeners}
            {...attributes}
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'grab',
              color: 'text.secondary',
              '&:active': { cursor: 'grabbing' },
            }}
          >
            <DragIndicator fontSize='small' />
          </Box>

          <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
            {getFormElementIcon(element.type, { fontSize: 'small' })}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant='body2' fontWeight='500' noWrap>
                {getFormElementSummary(element)}
              </Typography>
              <Chip label={element.type} size='small' sx={{ height: 18, fontSize: '0.65rem' }} />
            </Box>
            {element.type === 'FIELD' && element.data && (
              <Typography variant='caption' color='text.secondary' noWrap>
                {element.data.code || element.data.objectCode}
              </Typography>
            )}
            {element.type === 'BUTTON' && (element.command || element.actions?.length) && (
              <Typography variant='caption' color='text.secondary' noWrap>
                {element.command
                  ? `${element.command.name} (${element.command.objectCode})`
                  : `${element.actions?.length} actions`}
              </Typography>
            )}
            {element.type === 'SWIPE' && (element.command || element.actions?.length) && (
              <Typography variant='caption' color='text.secondary' noWrap>
                {element.command ? element.command.name : `${element.actions?.length} actions`}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {element.type !== 'DIVIDER' && (
              <IconButton size='small' onClick={onEdit} title='Edit'>
                <Edit fontSize='small' />
              </IconButton>
            )}
            <IconButton size='small' color='error' onClick={onDelete} title='Delete'>
              <Delete fontSize='small' />
            </IconButton>
          </Box>
        </Box>

        {isAccordion && !accordionParent && (
          <AccordionChildrenList
            accordionChildren={element.accordionChildren || []}
            isBottom={isBottom}
            accordionIndex={index}
            onEditElement={onEditElement}
            onDeleteElement={onDeleteElement}
            activeId={activeId}
          />
        )}
      </Paper>
    </Box>
  )
}

const AccordionChildrenList: FC<{
  accordionChildren: FormElement[]
  isBottom: boolean
  accordionIndex: number
  onEditElement: ConfiguredFormPanelProps['onEditElement']
  onDeleteElement: ConfiguredFormPanelProps['onDeleteElement']
  activeId: string | null
}> = ({ accordionChildren, isBottom, accordionIndex, onEditElement, onDeleteElement, activeId }) => {
  const containerId = buildAccordionContainerId(isBottom, accordionIndex)
  const sortableItems = accordionChildren.map((_, idx) => buildItemId(containerId, idx))

  const { setNodeRef, isOver } = useDroppable({
    id: `droppable-${containerId}`,
    data: {
      type: 'container',
      containerId,
      endIndex: accordionChildren.length,
    },
  })

  return (
    <SortableContext items={sortableItems} strategy={verticalListSortingStrategy}>
      <Box
        ref={setNodeRef}
        sx={{
          width: '100%',
          minHeight: 56,
          borderRadius: 1,
          border: '1px dashed',
          borderColor: isOver ? 'primary.main' : 'divider',
          backgroundColor: isOver ? 'action.hover' : 'action.selected',
          p: 1,
        }}
      >
        {accordionChildren.length === 0 ? (
          <Typography variant='caption' color='text.secondary'>
            Drag accordion elements here
          </Typography>
        ) : (
          accordionChildren.map((child, childIndex) => (
            <ElementSortableItem
              key={child.internalId || `${containerId}-${childIndex}`}
              element={child}
              containerId={containerId}
              index={childIndex}
              isBottom={isBottom}
              accordionParent={{ index: accordionIndex, isBottom }}
              onEdit={() => onEditElement(childIndex, isBottom, { index: accordionIndex, isBottom })}
              onDelete={() =>
                onDeleteElement(childIndex, isBottom, { index: accordionIndex, isBottom })
              }
              onEditElement={onEditElement}
              onDeleteElement={onDeleteElement}
              activeId={activeId}
            />
          ))
        )}
      </Box>
    </SortableContext>
  )
}

const TopLevelList: FC<{
  containerId: string
  elements: FormElement[]
  isBottom: boolean
  emptyText: string
  emptyHeight: number
  onEditElement: ConfiguredFormPanelProps['onEditElement']
  onDeleteElement: ConfiguredFormPanelProps['onDeleteElement']
  activeId: string | null
}> = ({
  containerId,
  elements,
  isBottom,
  emptyText,
  emptyHeight,
  onEditElement,
  onDeleteElement,
  activeId,
}) => {
  const sortableItems = elements.map((_, idx) => buildItemId(containerId, idx))

  const { setNodeRef, isOver } = useDroppable({
    id: `droppable-${containerId}`,
    data: {
      type: 'container',
      containerId,
      endIndex: elements.length,
    },
  })

  return (
    <SortableContext items={sortableItems} strategy={verticalListSortingStrategy}>
      <Box
        ref={setNodeRef}
        sx={{
          minHeight: emptyHeight + 20,
          borderRadius: 1,
          border: '2px dashed',
          borderColor: isOver ? 'primary.main' : 'divider',
          backgroundColor: isOver ? 'action.hover' : 'transparent',
          p: 1,
          transition: 'none',
        }}
      >
        {elements.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: emptyHeight,
            }}
          >
            <Typography variant='body2' color='text.secondary'>
              {emptyText}
            </Typography>
          </Box>
        ) : (
          elements.map((element, index) => (
            <ElementSortableItem
              key={element.internalId || `${containerId}-${index}`}
              element={element}
              containerId={containerId}
              index={index}
              isBottom={isBottom}
              onEdit={() => onEditElement(index, isBottom)}
              onDelete={() => onDeleteElement(index, isBottom)}
              onEditElement={onEditElement}
              onDeleteElement={onDeleteElement}
              activeId={activeId}
            />
          ))
        )}
      </Box>
    </SortableContext>
  )
}

export const ConfiguredFormPanel: FC<ConfiguredFormPanelProps> = ({
  elements,
  bottomElements,
  onEditElement,
  onDeleteElement,
  activeId,
}) => {
  return (
    <Paper
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          p: 2,
          borderBottom: theme => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant='h6'>Configured Form</Typography>
        <Typography variant='caption' color='text.secondary'>
          Reorder elements by dragging
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant='subtitle2' sx={{ mb: 1.5, color: 'text.secondary' }}>
            Form Elements
          </Typography>
          <TopLevelList
            containerId='MAIN_ELEMENTS'
            elements={elements}
            isBottom={false}
            emptyText='Drag elements here'
            emptyHeight={80}
            onEditElement={onEditElement}
            onDeleteElement={onDeleteElement}
            activeId={activeId}
          />
        </Box>

        <Box>
          <Typography variant='subtitle2' sx={{ mb: 1.5, color: 'text.secondary' }}>
            Bottom Elements (Action Buttons)
          </Typography>
          <TopLevelList
            containerId='BOTTOM_ELEMENTS'
            elements={bottomElements}
            isBottom={true}
            emptyText='Drag button elements here'
            emptyHeight={60}
            onEditElement={onEditElement}
            onDeleteElement={onDeleteElement}
            activeId={activeId}
          />
        </Box>
      </Box>
    </Paper>
  )
}
